// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- Hooks & Actions
import { useDateMathActions } from 'datastage/store/dateMath/useDateMath';
import { useDateMathData } from '../../hooks/useDateMathData';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Utilities
import { datastageApi } from 'datastage/api';
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { validateSpecialCharacters } from 'datastage/utils/validateSpecialCharacters';
import { cleanDateMathFilter, findStatusByValue, findTypeByValue } from './utils/dateMathSearchUtils';
// --- Styles
import './styles.scss';

/**
 * DateMath Search Form
 *
 * Features:
 * - Renders a filter form for Date Math Jobs (Job ID, Job Status, Job Type)
 * - Debounced auto-search on input change (400ms delay)
 * - Input validation for Job ID field
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function DateMathSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const { fetchDateMathJobs, updateDateMath, dateMathRange, dateMathSort, itemsPerPage } =
        useDateMathData();
    const { updateDateMathSearchForm, clearDateMath } = useDateMathActions();
    const { filterForm, jobStatusOptions, jobTypeOptions } = useSelector((s) => s.dateMath);

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        jobId: undefined,
    });

    // Fetch job status and type options if not already loaded
    useEffect(() => {
        if (
            !Array.isArray(jobStatusOptions) ||
            jobStatusOptions.length === 0 ||
            !Array.isArray(jobTypeOptions) ||
            jobTypeOptions.length === 0
        ) {
            (async () => {
                try {
                    const [statuses, types] = await Promise.all([
                        datastageApi.jobStatuses(false),
                        datastageApi.jobTypes(true),
                    ]);
                    updateDateMath({
                        jobStatusOptions: statuses,
                        jobTypeOptions: types,
                    });
                } catch (error) {
                    console.error('Error fetching job options:', error);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params, jobTypeOptions, jobStatusOptions);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateDateMath({
                    itemsPerPage: newPageSize,
                    dateMathRange: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateDateMathSearchForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Re-parse URL params when options load (needed for select values that depend on options)
    useEffect(() => {
        if (isInitialMount.current) return;
        if (!Array.isArray(jobTypeOptions) || jobTypeOptions.length === 0) return;
        if (!Array.isArray(jobStatusOptions) || jobStatusOptions.length === 0) return;

        const params = parseSearchParams(location.search);
        if (params.jobType || params.jobStatus) {
            const filterData = buildFilterFromParams(params, jobTypeOptions, jobStatusOptions);
            const mergedFilterData = { ...filterForm, ...filterData };
            updateDateMathSearchForm({ key: 'filterForm', data: filterData });

            // Handle pageSize if present
            let rangeOverride;
            if (params.pageSize) {
                const newPageSize = parseInt(params.pageSize, 10);
                if (!isNaN(newPageSize) && newPageSize > 0) {
                    rangeOverride = getItemRange(0, newPageSize);
                }
            }

            fetchData(mergedFilterData, rangeOverride);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobTypeOptions, jobStatusOptions]);

    /**
     * Builds filter form data from URL search params.
     * Converts string params to appropriate types and matches select options.
     */
    function buildFilterFromParams(params, typeOptions, statusOptions) {
        const filterData = {};

        if (params.jobId) filterData.jobId = params.jobId;

        // Match select options (only if options are loaded)
        if (params.jobType && Array.isArray(typeOptions) && typeOptions.length > 0) {
            const matchedType = findTypeByValue(typeOptions, params.jobType);
            if (matchedType) filterData.jobType = matchedType;
        }

        if (params.jobStatus && Array.isArray(statusOptions) && statusOptions.length > 0) {
            const matchedStatus = findStatusByValue(statusOptions, params.jobStatus);
            if (matchedStatus) filterData.jobStatus = matchedStatus;
        }

        return filterData;
    }

    /**
     * Fetches date math jobs data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        // Merge with current filterForm to include all fields
        const mergedFilter = { ...filterForm, ...filterData };
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchDateMathJobs(range, mergedFilter);
        if (data) {
            updateDateMath({
                jobs: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                dateMathRange: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearDateMath();
        setValidationErrors({ jobId: undefined });
        navigate(location.pathname);
    };

    /**
     * Handles input changes with validation and debounced search.
     * Validates Job ID field for special characters.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = validationErrors;

        // Validate Job ID field for special characters
        if (field === 'jobId') {
            const error = validateSpecialCharacters(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        }

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateDateMathSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateDateMath({ dateMathRange: firstPageRange, currentPage: 0 });
                const filtered = cleanDateMathFilter(newFilterForm, firstPageRange, dateMathSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    /**
     * Handles select changes with debounced search.
     */
    function handleSelectChange(field, value) {
        const newFilterForm = { ...filterForm, [field]: value };
        updateDateMathSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(validationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateDateMath({ dateMathRange: firstPageRange, currentPage: 0 });
                const filtered = cleanDateMathFilter(newFilterForm, firstPageRange, dateMathSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="datemath-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Job ID">
                    <Input
                        value={filterForm.jobId}
                        onChange={(e) => handleInputChange('jobId', e.target.value)}
                        width="auto"
                        id="jobId"
                        name="jobId"
                        placeholder="Enter"
                        className={validationErrors.jobId ? 'input-error' : ''}
                    />
                    {validationErrors.jobId && (
                        <div className="validation-error">{validationErrors.jobId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Job Status">
                    <Select
                        options={jobStatusOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleSelectChange('jobStatus', value)}
                        value={filterForm.jobStatus}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Job Type">
                    <Select
                        options={jobTypeOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleSelectChange('jobType', value)}
                        value={filterForm.jobType}
                    />
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
